import { type ActorRefFrom, type MachineImplementationsFrom, setup } from 'xstate5';

import { type Context, type Event, type Input } from './click-to-claim.machine.types';
import { Actions } from './setup/click-to-claim.machine.actions';
import { Actors } from './setup/click-to-claim.machine.actors';
import { generateInitialContext } from './setup/click-to-claim.machine.context';
import { Guards } from './setup/click-to-claim.machine.guards';
import * as Inputs from './setup/click-to-claim.machine.input';
import { getClaimErrorMessage, toErrorMessage } from './setup/click-to-claim.machine.utils';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Event,
  },
  actions: Actions,
  actors: Actors,
  guards: Guards,
});

// ─────────────────────────────────────────────────────────────────────────────

export const clickToClaimMachine = model.createMachine({
  id: 'click-to-claim',
  initial: 'claiming',
  context: (args) => generateInitialContext(args.input),
  states: {
    // ─── Claiming ────────────────────────────────────────────────────

    claiming: {
      invoke: {
        id: 'claimOffer',
        src: 'claimOffer',
        input: Inputs.getClaimOfferInput,
        onDone: [
          {
            target: 'claimed',
            guard: { type: 'checkIfClaimSucceeded', params: (args) => args.event.output },
            actions: [{ type: 'assignClaimedReward', params: (args) => args.event.output }],
          },
          {
            target: 'error.already-claimed',
            guard: { type: 'checkIfOfferAlreadyClaimed', params: (args) => args.event.output },
            actions: [
              {
                type: 'logFailedClaimDueToUserError',
                params: (args) => ({
                  errorType: 'already-claimed',
                  errorMessage: getClaimErrorMessage(args.event.output),
                }),
              },
            ],
          },
          {
            target: 'error.new-user-only',
            guard: { type: 'checkIfOfferNewUserOnly', params: (args) => args.event.output },
            actions: [
              {
                type: 'logFailedClaimDueToUserError',
                params: (args) => ({
                  errorType: 'new-user-only',
                  errorMessage: getClaimErrorMessage(args.event.output),
                }),
              },
            ],
          },
          {
            target: 'error.not-eligible',
            guard: { type: 'checkIfOfferNotEligible', params: (args) => args.event.output },
            actions: [
              {
                type: 'logFailedClaimDueToUserError',
                params: (args) => ({
                  errorType: 'not-eligible',
                  errorMessage: getClaimErrorMessage(args.event.output),
                }),
              },
            ],
          },
          {
            target: 'error.expired',
            guard: { type: 'checkIfOfferExpired', params: (args) => args.event.output },
            actions: [
              {
                type: 'logFailedClaimDueToUserError',
                params: (args) => ({
                  errorType: 'expired',
                  errorMessage: getClaimErrorMessage(args.event.output),
                }),
              },
            ],
          },
          {
            target: 'error.unknown-error',
            actions: [
              {
                type: 'logFailedClaim',
                params: (args) => ({ errorMessage: getClaimErrorMessage(args.event.output) }),
              },
            ],
          },
        ],
        onError: {
          target: 'error.unknown-error',
          actions: [
            { type: 'logFailedClaim', params: (args) => ({ errorMessage: toErrorMessage(args.event.error) }) }, // prettier-ignore
          ],
        },
      },
    },

    // ─── Outcomes ────────────────────────────────────────────────────

    claimed: {
      entry: ['logSuccessfulClaim'],
    },

    error: {
      initial: 'unknown-error',
      states: {
        'already-claimed': {},
        'not-eligible': {},
        'new-user-only': {},
        expired: {},
        'unknown-error': {},
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type ClickToClaimMachineActorRef = ActorRefFrom<typeof clickToClaimMachine>;
export type ClickToClaimMachineImplementations = MachineImplementationsFrom<typeof clickToClaimMachine>; // prettier-ignore
